import { useId } from 'react'
import { ModalOverlay } from './ui/ModalOverlay'
import { XCloseIcon, InfoIcon, PrimaryButton } from './ui'

interface PremiumWarningModalProps {
  onClose: () => void
  onConfirm: () => void
  confirmLabel?: string
}

const POLICY_MODAL_COPY = {
  title: 'Before you continue',
  paragraphs: [
    "It's important that you take reasonable care not to make a misrepresentation and to provide true, accurate and complete answers.",
    'Inaccurate answers may affect your ability to claim on the policy or could result in your policy being cancelled or treated as if it never existed.',
    "If you don't understand a question, please ask us for clarification. Further details about your duty can be found in our PDS.",
    'Any advice we provide is general in nature and does not take your personal circumstances into account. You should consider your needs and read the PDS and TMD before making a decision to buy insurance or make changes to your existing cover.',
  ],
} as const

export default function PremiumWarningModal({
  onClose,
  onConfirm,
  confirmLabel = 'Update policy',
}: PremiumWarningModalProps) {
  const titleId = useId()
  const copy = POLICY_MODAL_COPY

  const handleConfirm = () => {
    onClose()
    onConfirm()
  }

  return (
    <ModalOverlay
      open
      onClose={onClose}
      ariaLabelledBy={titleId}
      panelClassName="flex max-h-[calc(100vh-32px)] w-full max-w-[480px] flex-col overflow-hidden rounded-2xl bg-background-primary shadow-xl md:max-h-[calc(100vh-128px)]"
    >
      <div className="flex min-h-0 flex-1 flex-col items-center overflow-hidden">
        <div className="flex min-h-0 w-full flex-1 flex-col items-start overflow-y-auto overscroll-contain">
          <div className="relative flex w-full shrink-0 flex-col items-start gap-[16px] px-[16px] pb-[32px] pt-[20px] md:pb-[48px]">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-[12px] top-[12px] z-10 flex size-[44px] cursor-pointer items-center justify-center overflow-clip rounded-md p-[8px]"
              aria-label="Close"
            >
              <div className="relative size-6 shrink-0 overflow-clip text-text-tertiary-600">
                <XCloseIcon className="h-6 w-6" />
              </div>
            </button>

            <div className="relative flex size-12 shrink-0 items-center justify-center overflow-clip rounded-full bg-background-warning-secondary">
              <div className="relative size-6 shrink-0 overflow-clip text-warning-primary">
                <InfoIcon className="h-6 w-6" />
              </div>
            </div>

            <div className="relative flex w-full shrink-0 flex-col items-start gap-[8px]">
              <h2
                id={titleId}
                className="relative w-full shrink-0 font-display text-display-xs font-semibold leading-[32px] text-text-primary-900"
              >
                {copy.title}
              </h2>
              <div className="relative w-full shrink-0 font-body text-md font-regular leading-[24px] text-text-secondary-700">
                {copy.paragraphs.map((paragraph, index) => (
                  <p
                    key={paragraph}
                    className={index < copy.paragraphs.length - 1 ? 'mb-[16px]' : undefined}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="relative flex w-full shrink-0 flex-col items-start border-t border-border-secondary bg-background-primary px-[16px] pb-[16px] pt-[24px] md:border-0 md:pb-[24px]">
          <PrimaryButton onClick={handleConfirm} fullWidth>
            {confirmLabel}
          </PrimaryButton>
        </div>
      </div>
    </ModalOverlay>
  )
}
